import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useUserByEmail } from '../../api/users.js';
import { useTenantDefaultValidators } from '../../api/tenants.js';

const MAX_COMMENT = 4000;
const OTHER = '__other__';

interface Props {
  invoiceNumber: string;
  tenantId: string;
  busy: boolean;
  onSubmit: (payload: { validatorUid: string; requestComment: string | null }) => void;
  onClose: () => void;
}

export function RequestValidationModal({
  invoiceNumber,
  tenantId,
  busy,
  onSubmit,
  onClose,
}: Props) {
  const defaults = useTenantDefaultValidators(tenantId);
  // Selection key: uid of a default validator, or OTHER for free-text.
  // Auto-select the first default when the pool resolves; stay OTHER while empty.
  const [selection, setSelection] = useState<string>(OTHER);
  const [autoSelected, setAutoSelected] = useState(false);
  const [email, setEmail] = useState('');
  const [comment, setComment] = useState('');
  const lookup = useUserByEmail(email);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (autoSelected) return;
    if (defaults.length === 0) return;
    const first = defaults[0]!;
    setSelection(first.uid);
    setEmail(first.email ?? '');
    setAutoSelected(true);
  }, [defaults, autoSelected]);

  useEffect(() => {
    inputRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleSelectionChange(next: string) {
    setSelection(next);
    if (next === OTHER) {
      setEmail('');
      // Re-focus the freed input so the user can type immediately.
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      const v = defaults.find((d) => d.uid === next);
      setEmail(v?.email ?? '');
    }
  }

  const inputDisabled = selection !== OTHER;
  const found = lookup.data ?? null;
  const searching = email.includes('@') && email.length > 3 && lookup.isLoading;
  const notFound =
    !lookup.isLoading && email.includes('@') && email.length > 3 && lookup.data === null;
  const submitDisabled = busy || !found;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitDisabled || !found) return;
    const trimmed = comment.trimEnd();
    onSubmit({
      validatorUid: found.uid,
      requestComment: trimmed.length > 0 ? trimmed : null,
    });
  }

  return (
    <div
      className="modal__backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="request-validation-modal"
    >
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="rv-modal-title">
        <header className="modal__head">
          <h2 id="rv-modal-title" className="display modal__title">
            Demander une validation · {invoiceNumber}
          </h2>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="Fermer"
            data-testid="rv-close"
          >
            ×
          </button>
        </header>

        <form className="dispute-form" onSubmit={handleSubmit}>
          {defaults.length > 0 ? (
            <label className="dispute-form__label" htmlFor="rv-suggested">
              <span className="eyebrow">Validateur suggéré</span>
              <select
                id="rv-suggested"
                className="modal__search"
                value={selection}
                onChange={(e) => handleSelectionChange(e.target.value)}
                data-testid="rv-suggested"
              >
                <option value={OTHER}>Autre — saisir email</option>
                {defaults.map((d) => (
                  <option key={d.uid} value={d.uid}>
                    {(d.displayName ?? d.email ?? d.uid) + (d.email ? ` · ${d.email}` : '')}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <label className="dispute-form__label" htmlFor="rv-email">
            <span className="eyebrow">Email du validateur (n'importe quel employé Altyor)</span>
            <input
              id="rv-email"
              ref={inputRef}
              type="email"
              className="modal__search"
              placeholder="prenom.nom@altyor.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="rv-email"
              autoComplete="off"
              disabled={inputDisabled}
            />
          </label>

          <div className="rv-resolution" aria-live="polite" data-testid="rv-resolution">
            {searching ? (
              <span className="muted">Recherche…</span>
            ) : found ? (
              <span className="rv-resolution__ok" data-testid="rv-found">
                <span className="rv-resolution__avatar">
                  {(found.displayName ?? found.email ?? '?').slice(0, 2).toUpperCase()}
                </span>
                <span>
                  <strong>{found.displayName ?? found.email}</strong>{' '}
                  {found.displayName && found.email ? (
                    <span className="muted">· {found.email}</span>
                  ) : null}
                </span>
              </span>
            ) : notFound ? (
              <span className="rv-resolution__none" data-testid="rv-not-found">
                Aucun utilisateur trouvé pour cet email.
              </span>
            ) : (
              <span className="muted">Tapez un email Altyor pour résoudre.</span>
            )}
          </div>

          <label className="dispute-form__label" htmlFor="rv-comment">
            <span className="eyebrow">Commentaire (optionnel)</span>
            <textarea
              id="rv-comment"
              className="comments-composer__textarea"
              placeholder="Ex. : pouvez-vous valider ce montant avant accusé de réception ?"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={MAX_COMMENT + 100}
              rows={3}
              data-testid="rv-comment"
            />
            <span className="muted comments-composer__counter">
              {comment.trimEnd().length}/{MAX_COMMENT}
            </span>
          </label>

          <footer className="modal__foot dispute-form__foot">
            <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>
              Annuler
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitDisabled}
              data-testid="rv-submit"
            >
              {busy ? 'Envoi…' : 'Demander la validation'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
